/* QuoteCompare.jsx — gratis tool om 3 zonnepanelen-offertes te vergelijken
   Route: /offertes-vergelijken
   i18n: all UI strings are translated via `quote_compare` namespace.
*/

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Upload, FileText, X, Loader2, CheckCircle2, AlertTriangle, ArrowRight,
  Sparkles, ShieldCheck, Star, Download, MessageCircleQuestion, Lightbulb,
  TrendingDown, Send, Share2, Copy, Mail, MessageCircle,
} from "lucide-react";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ZAxis, Label,
} from "recharts";
import { apiClient } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

const MAX_FILES = 3;
const MAX_SIZE_MB = 12;
const ACCEPT = ".pdf,.png,.jpg,.jpeg";

const useQ = () => useTranslation().t;

export default function QuoteCompare() {
  const t = useQ();
  const { i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const dropRef = useRef(null);
  const inputRef = useRef(null);

  const addFiles = (incoming) => {
    const accepted = incoming.filter((f) => {
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(t("quote_compare.toast.too_big", { name: f.name, max: MAX_SIZE_MB }));
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...accepted].slice(0, MAX_FILES));
  };

  const removeFile = (idx) => setFiles((p) => p.filter((_, i) => i !== idx));

  // Drag visual feedback
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const on = (e) => { e.preventDefault(); el.classList.add("ring-2", "ring-[#FCBF49]"); };
    const off = () => { el.classList.remove("ring-2", "ring-[#FCBF49]"); };
    const drop = (e) => {
      e.preventDefault(); off();
      const dropped = Array.from(e.dataTransfer.files || []);
      addFiles(dropped);
    };
    el.addEventListener("dragover", on);
    el.addEventListener("dragleave", off);
    el.addEventListener("drop", drop);
    return () => {
      el.removeEventListener("dragover", on);
      el.removeEventListener("dragleave", off);
      el.removeEventListener("drop", drop);
    };
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) { toast.error(t("quote_compare.toast.email_required")); return; }
    if (files.length === 0) { toast.error(t("quote_compare.toast.file_required")); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("email", email);
      form.append("name", name);
      form.append("language", (i18n.language || "es").slice(0, 2));
      files.forEach((f) => form.append("files", f));
      const r = await apiClient.post("/quote-compare/analyze", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      try {
        sessionStorage.setItem("qc_name", name || "");
        sessionStorage.setItem("qc_email", email || "");
      } catch { /* ignore */ }
      setResult(r.data);
      toast.success(t("quote_compare.toast.success"));
      setTimeout(() => window.scrollTo({ top: 500, behavior: "smooth" }), 200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("quote_compare.toast.error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[#003049] font-sans">
      <Header />
      {!result ? (
        <UploadFlow {...{ email, setEmail, name, setName, files, addFiles, removeFile, submit, busy, dropRef, inputRef }} />
      ) : (
        <Results result={result} onReset={() => { setResult(null); setFiles([]); }} />
      )}
      <Footer />
    </div>
  );
}

function Header() {
  const t = useQ();
  return (
    <header className="bg-white border-b border-[#E5E9EE] sticky top-0 z-30">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center">
          <img src="/img/doctorsol-logo.svg" alt="DoctorSol" className="h-12 w-auto" draggable={false} />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" className="hidden sm:inline text-sm text-[#52606E] hover:text-[#003049]">
            {t("quote_compare.header_back")}
          </Link>
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function UploadFlow({ email, setEmail, name, setName, files, addFiles, removeFile, submit, busy, dropRef, inputRef }) {
  const t = useQ();
  return (
    <>
      <section className="bg-[#F4F7FA] py-16 lg:py-24 border-b border-[#EFF2F5]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-[#1F7A4B]/20 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-4 h-4 text-[#1F7A4B]" />
            <span className="text-xs font-bold text-[#1F7A4B] uppercase tracking-wider">{t("quote_compare.hero.badge")}</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-5">
            {t("quote_compare.hero.title_1")}
            <br /><span className="text-[#FCBF49]">{t("quote_compare.hero.title_2")}</span>
          </h1>
          <p className="text-lg text-[#52606E] max-w-2xl mx-auto leading-relaxed">
            {t("quote_compare.hero.subtitle")}
          </p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <form onSubmit={submit} className="bg-white rounded-2xl shadow-lg shadow-[#003049]/5 border border-[#EFF2F5] p-7 lg:p-10 space-y-6">

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("quote_compare.form.name_label")} value={name} onChange={setName} testid="qc-name" placeholder={t("quote_compare.form.name_placeholder")} />
              <Field label={t("quote_compare.form.email_label")} value={email} onChange={setEmail} testid="qc-email" placeholder={t("quote_compare.form.email_placeholder")} type="email" required />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-2 font-semibold">
                {t("quote_compare.form.files_label", { max: MAX_FILES })}
              </label>
              <div ref={dropRef} data-testid="qc-dropzone"
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-[#E5E9EE] hover:border-[#FCBF49] rounded-xl p-8 lg:p-10 text-center cursor-pointer transition-colors bg-[#F7F8FA]">
                <Upload className="w-10 h-10 text-[#FCBF49] mx-auto mb-3" />
                <p className="font-semibold text-[#003049] mb-1">{t("quote_compare.form.drop_title")}</p>
                <p className="text-sm text-[#52606E]">{t("quote_compare.form.drop_sub", { size: MAX_SIZE_MB })}</p>
                <input ref={inputRef} type="file" multiple accept={ACCEPT}
                  onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
                  className="hidden" data-testid="qc-fileinput" />
              </div>
            </div>

            {files.length > 0 && (
              <ul className="space-y-2" data-testid="qc-filelist">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 bg-[#FFFBEF] border border-[#FCBF49]/30 rounded-lg px-4 py-3">
                    <FileText className="w-5 h-5 text-[#FCBF49] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#003049] truncate">{f.name}</div>
                      <div className="text-xs text-[#52606E]">{(f.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <button type="button" onClick={() => removeFile(i)}
                      className="text-[#52606E] hover:text-[#D62828] p-1" data-testid={`qc-remove-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <button type="submit" disabled={busy || files.length === 0 || !email}
              data-testid="qc-submit"
              className="w-full bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-7 py-4 flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> {t("quote_compare.form.submit_busy")}
                </>
              ) : (
                <>{t("quote_compare.form.submit_idle")} <ArrowRight className="w-5 h-5" /></>
              )}
            </button>

            <p className="text-xs text-[#52606E] text-center">
              {t("quote_compare.form.disclaimer")}
            </p>
          </form>

          <div className="mt-10 grid sm:grid-cols-3 gap-5 text-sm text-[#003049]">
            <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#1F7A4B]" /> {t("quote_compare.trust.independent")}</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#1F7A4B]" /> {t("quote_compare.trust.fast")}</div>
            <div className="flex items-center gap-2"><Star className="w-5 h-5 text-[#1F7A4B]" /> {t("quote_compare.trust.reviews")}</div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-1.5 font-semibold">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} required={required} data-testid={testid}
        className="w-full bg-[#F7F8FA] border border-[#E5E9EE] rounded-md px-4 py-3 outline-none focus:border-[#FCBF49] focus:ring-2 focus:ring-[#FCBF49]/30 text-[#003049]" />
    </div>
  );
}

function Results({ result, onReset, sharedView = false }) {
  const t = useQ();
  const [shareOpen, setShareOpen] = useState(false);
  const { parsed = [], advisory = {}, token } = result;
  const bestIdx = Number.isInteger(advisory.best_value_index) ? advisory.best_value_index
    : (typeof advisory.best_value_index === "string" ? parseInt(advisory.best_value_index, 10) : -1);

  const downloadPdf = async () => {
    try {
      const r = await apiClient.get("/quote-compare/pdf", { params: { token }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = t("quote_compare.pdf_filename");
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("quote_compare.toast.pdf_error")); }
  };

  const subtitleKey = parsed.length === 1 ? "quote_compare.results.subtitle_one" : "quote_compare.results.subtitle_other";

  return (
    <section className="py-12 lg:py-16 bg-[#F7F8FA] min-h-screen">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("quote_compare.results.title")}</h1>
            <p className="text-[#52606E] mt-1">{t(subtitleKey, { count: parsed.length })}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!sharedView && (
              <button onClick={() => setShareOpen(true)} data-testid="qc-share-open"
                className="inline-flex items-center gap-2 bg-[#1F7A4B] hover:bg-[#0E5934] text-white rounded-md px-5 py-2.5 font-semibold text-sm">
                <Share2 className="w-4 h-4" /> {t("quote_compare.share.button")}
              </button>
            )}
            <button onClick={downloadPdf} data-testid="qc-download-pdf"
              className="inline-flex items-center gap-2 bg-[#003049] hover:bg-[#001D2B] text-white rounded-md px-5 py-2.5 font-semibold text-sm">
              <Download className="w-4 h-4" /> {t("quote_compare.results.download")}
            </button>
            {!sharedView && onReset && (
              <button onClick={onReset} className="inline-flex items-center gap-2 border border-[#E5E9EE] hover:border-[#003049] text-[#003049] rounded-md px-5 py-2.5 font-semibold text-sm">
                {t("quote_compare.results.new")}
              </button>
            )}
          </div>
        </div>

        {advisory.summary && (
          <div className="bg-white rounded-2xl border border-[#EFF2F5] p-6 lg:p-8 mb-6">
            <div className="text-xs uppercase tracking-wider text-[#FCBF49] font-bold mb-2">{t("quote_compare.results.summary")}</div>
            <p className="text-lg text-[#003049] leading-relaxed">{advisory.summary}</p>
          </div>
        )}

        {bestIdx >= 0 && bestIdx < parsed.length && (
          <div className="bg-gradient-to-br from-[#1F7A4B] to-[#0E5934] text-white rounded-2xl p-6 lg:p-8 mb-6">
            <div className="text-xs uppercase tracking-wider text-[#FCBF49] font-bold mb-2">{t("quote_compare.results.best_label")}</div>
            <div className="flex items-start gap-4">
              <Star className="w-8 h-8 text-[#FCBF49] fill-[#FCBF49] shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold mb-2">{parsed[bestIdx].installer_name || t("quote_compare.offer_fallback", { n: bestIdx + 1 })}</h2>
                <p className="text-white/90 leading-relaxed">{advisory.best_value_reason}</p>
              </div>
            </div>
          </div>
        )}

        <div className={`grid gap-4 mb-6 ${parsed.length === 1 ? "grid-cols-1" : parsed.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
          {parsed.map((q, i) => (
            <QuoteCard key={i} q={q} isBest={i === bestIdx} index={i} />
          ))}
        </div>

        <PriceScatter parsed={parsed} bestIdx={bestIdx} />

        <ComparisonTable parsed={parsed} bestIdx={bestIdx} />

        {advisory.key_differences?.length > 0 && (
          <section className="bg-white rounded-2xl border border-[#EFF2F5] p-6 lg:p-8 mt-8" data-testid="qc-key-diffs">
            <h3 className="text-xl font-bold mb-5 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-[#FCBF49]" /> {t("quote_compare.diffs.title")}
            </h3>
            <ul className="space-y-4">
              {advisory.key_differences.map((d, i) => (
                <li key={i}>
                  <div className="font-semibold text-[#003049] mb-1">{d.topic}</div>
                  <div className="text-[#52606E] leading-relaxed">{d.explanation}</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {advisory.warnings?.length > 0 && (
          <section className="bg-[#FFF5F5] border border-[#D62828]/20 rounded-2xl p-6 lg:p-8 mt-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-[#003049]">
              <AlertTriangle className="w-5 h-5 text-[#D62828]" /> {t("quote_compare.warnings.title")}
            </h3>
            <ul className="space-y-2">
              {advisory.warnings.map((w, i) => (
                <li key={i} className="flex gap-3"><span className="text-[#D62828] mt-1">•</span><span className="text-[#003049] leading-relaxed">{w}</span></li>
              ))}
            </ul>
          </section>
        )}

        {advisory.questions_to_ask?.length > 0 && (
          <section className="bg-[#FFFBEF] border border-[#FCBF49]/30 rounded-2xl p-6 lg:p-8 mt-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-[#003049]">
              <MessageCircleQuestion className="w-5 h-5 text-[#FCBF49]" /> {t("quote_compare.questions.title")}
            </h3>
            <ul className="space-y-2">
              {advisory.questions_to_ask.map((q, i) => (
                <li key={i} className="flex gap-3"><span className="text-[#FCBF49] mt-1 font-bold">{i + 1}.</span><span className="text-[#003049] leading-relaxed">{q}</span></li>
              ))}
            </ul>
          </section>
        )}

        <SecondOpinionCTA token={token} result={result} sharedView={sharedView} />

        {shareOpen && <ShareModal token={token} onClose={() => setShareOpen(false)} />}
      </div>
    </section>
  );
}

export { Results };

function QuoteCard({ q, isBest, index }) {
  const t = useQ();
  const get = (path, fallback = "—") => {
    const parts = path.split(".");
    let cur = q;
    for (const p of parts) {
      if (cur && typeof cur === "object") cur = cur[p];
      else return fallback;
    }
    return cur ?? fallback;
  };
  return (
    <div data-testid={`qc-card-${index}`}
      className={`rounded-2xl p-6 ${isBest ? "bg-white border-2 border-[#FCBF49] shadow-lg" : "bg-white border border-[#EFF2F5]"}`}>
      {isBest && (
        <div className="inline-flex items-center gap-1.5 bg-[#FCBF49] text-[#003049] rounded-full px-3 py-1 text-xs font-bold mb-3">
          <Star className="w-3.5 h-3.5 fill-[#003049]" /> {t("quote_compare.results.best_pill")}
        </div>
      )}
      <div className="font-bold text-lg text-[#003049] mb-1 truncate">{q.installer_name || t("quote_compare.offer_fallback", { n: index + 1 })}</div>
      <div className="text-xs text-[#52606E] mb-4 truncate">{q._file_name}</div>
      <div className="space-y-2 text-sm">
        <Row label={t("quote_compare.card.power")} value={`${get("system_kwp")} kWp`} />
        <Row label={t("quote_compare.card.panels")} value={`${get("panels.count")} × ${get("panels.brand")}`} />
        <Row label={t("quote_compare.card.inverter")} value={`${get("inverter.brand")} ${get("inverter.kw")} kW`} />
        <Row label={t("quote_compare.card.battery")} value={String(get("battery.included")).toLowerCase() === "true" ? `${get("battery.capacity_kwh")} kWh` : t("quote_compare.card.battery_none")} />
        <Row label={t("quote_compare.card.price")} value={`€ ${get("price.total_eur")}`} bold />
        <Row label={t("quote_compare.card.per_kwp")} value={`€ ${get("price.price_per_kwp")}`} />
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-[#52606E] text-xs uppercase tracking-wider">{label}</span>
      <span className={`text-right ${bold ? "font-bold text-[#003049]" : "text-[#003049]"}`}>{value}</span>
    </div>
  );
}

function ComparisonTable({ parsed, bestIdx }) {
  const t = useQ();
  const yr = t("quote_compare.table.year_short");
  const wk = t("quote_compare.table.week_short");
  const r = (k) => t(`quote_compare.table.rows.${k}`);
  const rows = [
    [r("panel_count"), parsed.map(q => q.panels?.count ?? "—")],
    [r("panel_brand"), parsed.map(q => q.panels?.brand ?? "—")],
    [r("watt_per_panel"), parsed.map(q => q.panels?.watt_per_panel ? `${q.panels.watt_per_panel} W` : "—")],
    [r("warranty_panel"), parsed.map(q => q.panels?.warranty_years_product ? `${q.panels.warranty_years_product} ${yr}` : "—")],
    [r("inverter_brand"), parsed.map(q => q.inverter?.brand ?? "—")],
    [r("inverter_type"), parsed.map(q => q.inverter?.type ?? "—")],
    [r("inverter_phases"), parsed.map(q => q.inverter?.phases ?? "—")],
    [r("warranty_inverter"), parsed.map(q => q.inverter?.warranty_years ? `${q.inverter.warranty_years} ${yr}` : "—")],
    [r("battery"), parsed.map(q => String(q.battery?.included).toLowerCase() === "true" ? `${q.battery?.capacity_kwh} kWh ${q.battery?.brand || ""}` : t("quote_compare.card.battery_none"))],
    [r("boletin"), parsed.map(q => q.extras?.boletin_electrico ?? "—")],
    [r("subvenciones"), parsed.map(q => q.extras?.subvenciones ?? "—")],
    [r("warranty_install"), parsed.map(q => q.warranty_installation?.years ? `${q.warranty_installation.years} ${yr}` : "—")],
    [r("delivery"), parsed.map(q => q.delivery?.estimated_weeks ? `${q.delivery.estimated_weeks} ${wk}` : "—")],
    [r("iva"), parsed.map(q => q.price?.iva_included ?? "—")],
    [r("total"), parsed.map(q => `€ ${q.price?.total_eur ?? "—"}`)],
    [r("per_kwp"), parsed.map(q => `€ ${q.price?.price_per_kwp ?? "—"}`)],
  ];

  return (
    <section className="bg-white rounded-2xl border border-[#EFF2F5] overflow-hidden" data-testid="qc-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#003049] text-white">
              <th className="text-left px-5 py-4 font-semibold">{t("quote_compare.table.detail")}</th>
              {parsed.map((q, i) => (
                <th key={i} className="text-left px-5 py-4 font-semibold">
                  {q.installer_name || t("quote_compare.offer_fallback", { n: i + 1 })}
                  {i === bestIdx && <span className="ml-1 text-[#FCBF49]">★</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, vals], i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}>
                <td className="px-5 py-3 font-semibold text-[#003049]">{label}</td>
                {vals.map((v, j) => (
                  <td key={j} className={`px-5 py-3 text-[#003049] ${j === bestIdx ? "bg-[#FFFBEF]" : ""}`}>{v ?? "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SecondOpinionCTA({ token, result, sharedView = false }) {
  const t = useQ();
  const initialName = result?.name || result?.lead?.name || "";
  const initialEmail = result?.email || result?.lead?.email || "";
  // result for the standalone analyze call contains the parsed quotes etc, but not name/email
  // — those came from the original form submission. We pull them from sessionStorage.
  let storedName = "";
  let storedEmail = "";
  try {
    storedName = sessionStorage.getItem("qc_name") || "";
    storedEmail = sessionStorage.getItem("qc_email") || "";
  } catch { /* ignore */ }

  const name = initialName || storedName;
  const email = initialEmail || storedEmail;

  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (sharedView) return;
    setBusy(true);
    try {
      const r = await apiClient.post("/quote-compare/free-second-opinion", { token });
      if (r.data?.email) {
        try { sessionStorage.setItem("qc_email", r.data.email); } catch { /* ignore */ }
      }
      setDone(true);
      toast.success(t("quote_compare.upsell.success_toast"));
    } catch {
      toast.error(t("quote_compare.upsell.error_toast"));
    } finally { setBusy(false); }
  };

  // In shared view there is no name/email of the viewer, so we link them to the funnel instead.
  if (sharedView) {
    return (
      <section className="mt-10 bg-gradient-to-br from-[#003049] to-[#001D2B] text-white rounded-3xl p-8 lg:p-12 shadow-2xl" data-testid="qc-upsell-shared">
        <div className="inline-flex items-center gap-2 bg-[#FCBF49]/20 rounded-full px-4 py-1.5 mb-4">
          <Sparkles className="w-4 h-4 text-[#FCBF49]" />
          <span className="text-xs font-bold text-[#FCBF49] uppercase tracking-wider">{t("quote_compare.upsell.badge")}</span>
        </div>
        <h2 className="text-3xl lg:text-4xl font-bold leading-tight mb-4">{t("quote_compare.upsell.title")}</h2>
        <p className="text-white/85 text-lg leading-relaxed mb-7 max-w-3xl">{t("quote_compare.upsell.body")}</p>
        <Link to="/offertes-vergelijken" data-testid="qc-upsell-shared-link"
          className="inline-flex items-center gap-2 bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-7 py-4 transition">
          {t("quote_compare.upsell.cta")} <ArrowRight className="w-5 h-5" />
        </Link>
      </section>
    );
  }

  if (done) {
    return (
      <section className="mt-10 bg-[#1F7A4B]/10 border-2 border-[#1F7A4B]/30 rounded-3xl p-8 lg:p-12 text-center" data-testid="qc-upsell-done">
        <div className="w-16 h-16 rounded-full bg-[#1F7A4B]/20 mx-auto mb-5 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-[#1F7A4B]" />
        </div>
        <h3 className="text-3xl font-bold text-[#003049] mb-3">{t("quote_compare.upsell.success_title")}</h3>
        <p className="text-[#52606E] leading-relaxed max-w-2xl mx-auto"
           dangerouslySetInnerHTML={{ __html: t("quote_compare.upsell.success_body", { email }) }} />
      </section>
    );
  }

  return (
    <section className="mt-10 bg-gradient-to-br from-[#003049] to-[#001D2B] text-white rounded-3xl p-8 lg:p-12 shadow-2xl overflow-hidden relative" data-testid="qc-upsell">
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#FCBF49]/10 rounded-full blur-3xl" aria-hidden="true" />
      <div className="relative grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 bg-[#FCBF49]/20 rounded-full px-4 py-1.5 mb-5">
            <Sparkles className="w-4 h-4 text-[#FCBF49]" />
            <span className="text-xs font-bold text-[#FCBF49] uppercase tracking-wider">{t("quote_compare.upsell.badge")}</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold leading-tight mb-5">
            {t("quote_compare.upsell.title")}
          </h2>
          <p className="text-white/85 text-lg leading-relaxed mb-5">
            {t("quote_compare.upsell.body")}
          </p>
          <ul className="space-y-2.5 mb-3">
            {(t("quote_compare.upsell.bullets", { returnObjects: true }) || []).map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-white/90">
                <CheckCircle2 className="w-5 h-5 text-[#FCBF49] shrink-0 mt-0.5" strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white text-[#003049] rounded-2xl p-6 shadow-xl">
            <div className="inline-flex items-center gap-1.5 bg-[#1F7A4B]/10 text-[#1F7A4B] rounded-full px-3 py-1 text-xs font-bold mb-4">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={3} /> {t("quote_compare.upsell.free_pill")}
            </div>

            <div className="text-xs uppercase tracking-wider text-[#52606E] font-semibold mb-2">
              {t("quote_compare.upsell.confirm_label")}
            </div>
            <div className="space-y-2 mb-5">
              <div className="bg-[#F7F8FA] rounded-lg px-4 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-[#52606E] font-semibold">{t("quote_compare.upsell.name_label")}</div>
                <div className="font-semibold text-[#003049] truncate" data-testid="qc-upsell-name">{name || "—"}</div>
              </div>
              <div className="bg-[#F7F8FA] rounded-lg px-4 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-[#52606E] font-semibold">{t("quote_compare.upsell.email_label")}</div>
                <div className="font-semibold text-[#003049] truncate" data-testid="qc-upsell-email">{email || "—"}</div>
              </div>
            </div>

            <button onClick={submit} disabled={busy || !email} data-testid="qc-upsell-submit"
              className="w-full bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-6 py-4 flex items-center justify-center gap-2 transition disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("quote_compare.upsell.cta")} <ArrowRight className="w-5 h-5" /></>}
            </button>
            <p className="text-xs text-[#52606E] text-center mt-3">{t("quote_compare.upsell.free_note")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}


function UpsellCTA_LEGACY_UNUSED() {
  return null;
}

const TooltipBox = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-[#EFF2F5] rounded-lg shadow-lg p-3 text-sm">
      <div className="font-bold text-[#003049]">{d.name}{d.isBest && " ★"}</div>
      <div className="text-[#52606E]">{d.x} kWp · € {d.y.toFixed(0)}/kWp</div>
    </div>
  );
};

const ScatterDot = (props) => {
  const d = props.payload;
  const fill = d.isBest ? "#FCBF49" : "#003049";
  const stroke = d.isBest ? "#003049" : "#FCBF49";
  return (
    <g>
      <circle cx={props.cx} cy={props.cy} r={11} fill={fill} stroke={stroke} strokeWidth={2} />
      <text x={props.cx} y={props.cy - 16} textAnchor="middle" fontSize={10} fontWeight={600} fill="#003049">
        {d.name.length > 18 ? d.name.slice(0, 16) + "…" : d.name}
      </text>
    </g>
  );
};

function PriceScatter({ parsed, bestIdx }) {
  const t = useQ();
  const data = parsed
    .map((q, i) => {
      const kwp = parseFloat(q.system_kwp);
      const ppk = parseFloat(q.price?.price_per_kwp);
      if (!isFinite(kwp) || !isFinite(ppk)) return null;
      return {
        x: kwp, y: ppk, name: q.installer_name || t("quote_compare.offer_fallback", { n: i + 1 }),
        isBest: i === bestIdx, index: i,
      };
    })
    .filter(Boolean);

  if (data.length === 0) return null;

  const avgY = data.reduce((s, d) => s + d.y, 0) / data.length;

  return (
    <section className="bg-white rounded-2xl border border-[#EFF2F5] p-6 lg:p-8 mb-6" data-testid="qc-scatter">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#FCBF49] font-bold mb-1">{t("quote_compare.scatter.label")}</div>
          <h3 className="text-xl font-bold">{t("quote_compare.scatter.title")}</h3>
          <p className="text-sm text-[#52606E] mt-1">{t("quote_compare.scatter.subtitle")}</p>
        </div>
        <div className="text-xs text-[#52606E]">
          {t("quote_compare.scatter.avg")}: <span className="font-bold text-[#003049]">€ {avgY.toFixed(0)}/kWp</span>
        </div>
      </div>

      <div className="h-72 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E9EE" />
            <XAxis type="number" dataKey="x" name="kWp" stroke="#52606E" fontSize={11}>
              <Label value={t("quote_compare.scatter.x_label")} position="bottom" offset={20} fill="#52606E" fontSize={12} />
            </XAxis>
            <YAxis type="number" dataKey="y" name="€/kWp" stroke="#52606E" fontSize={11}>
              <Label value={t("quote_compare.scatter.y_label")} angle={-90} position="left" offset={40} fill="#52606E" fontSize={12} />
            </YAxis>
            <ZAxis range={[200, 200]} />
            <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<TooltipBox />} />
            <ReferenceLine y={avgY} stroke="#52606E" strokeDasharray="5 5" label={{ value: t("quote_compare.scatter.avg"), position: "insideTopRight", fill: "#52606E", fontSize: 10 }} />
            <Scatter data={data} shape="circle">
              {data.map((d, i) => (
                <Scatter key={i} dataKey="y" />
              ))}
            </Scatter>
            <Scatter data={data} fill="#003049" shape={ScatterDot} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-[#52606E] mt-3">
        <TrendingDown className="w-3.5 h-3.5 inline-block mr-1" />
        {t("quote_compare.scatter.footer")}
      </p>
    </section>
  );
}


function CounterOfferBlock({ token, sharedView = false }) {
  const t = useQ();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const bullets = t("quote_compare.counter.bullets", { returnObjects: true }) || [];

  // In shared view, the friend should start their OWN free comparison, not attach to the original lead.
  if (sharedView) {
    return (
      <section className="mt-8 bg-white border-2 border-[#1F7A4B]/30 rounded-2xl p-6 lg:p-10 shadow-md" data-testid="qc-shared-cta">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-[#1F7A4B]/10 text-[#1F7A4B] rounded-full px-4 py-1.5 mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-wider">{t("quote_compare.share.viewer_cta_title")}</span>
          </div>
          <h3 className="text-2xl lg:text-3xl font-bold leading-tight mb-3 text-[#003049] max-w-2xl mx-auto">
            {t("quote_compare.share.viewer_cta_title")}
          </h3>
          <p className="text-[#52606E] leading-relaxed mb-6 max-w-2xl mx-auto">
            {t("quote_compare.share.viewer_cta_body")}
          </p>
          <Link to="/offertes-vergelijken" data-testid="qc-shared-cta-link"
            className="inline-flex items-center gap-2 bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-7 py-4 transition">
            {t("quote_compare.share.viewer_cta_button")} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    );
  }

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await apiClient.post("/quote-compare/counter-offer", { token, phone, message });
      setDone(true);
      toast.success(t("quote_compare.counter.toast_success"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("quote_compare.counter.toast_error"));
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-8 bg-white border-2 border-[#FCBF49]/40 rounded-2xl p-6 lg:p-10 shadow-md" data-testid="qc-counter-offer">
      <div className="grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 bg-[#FCBF49]/20 text-[#003049] rounded-full px-4 py-1.5 mb-4">
            <Sparkles className="w-4 h-4 text-[#FCBF49]" />
            <span className="text-xs font-bold uppercase tracking-wider">{t("quote_compare.counter.badge")}</span>
          </div>
          <h3 className="text-2xl lg:text-3xl font-bold leading-tight mb-4 text-[#003049]">
            {t("quote_compare.counter.title")}
          </h3>
          <p className="text-[#52606E] leading-relaxed mb-5">
            {t("quote_compare.counter.body")}
          </p>
          <ul className="space-y-2.5 mb-5">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[#003049]">
                <CheckCircle2 className="w-5 h-5 text-[#1F7A4B] shrink-0 mt-0.5" strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          {done ? (
            <div className="bg-[#1F7A4B]/10 border border-[#1F7A4B]/30 rounded-xl p-7 text-center" data-testid="qc-counter-done">
              <CheckCircle2 className="w-10 h-10 text-[#1F7A4B] mx-auto mb-3" />
              <h4 className="text-xl font-bold mb-2 text-[#003049]">{t("quote_compare.counter.done_title")}</h4>
              <p className="text-sm text-[#52606E]">{t("quote_compare.counter.done_body")}</p>
            </div>
          ) : !open ? (
            <button onClick={() => setOpen(true)} data-testid="qc-counter-open"
              className="w-full bg-[#003049] hover:bg-[#001D2B] text-white font-bold rounded-xl px-7 py-5 flex items-center justify-center gap-2 transition shadow-lg">
              <Send className="w-5 h-5" />
              {t("quote_compare.counter.open")}
            </button>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="qc-counter-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-1.5 font-semibold">{t("quote_compare.counter.phone_label")}</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+34..."
                  data-testid="qc-counter-phone"
                  className="w-full bg-[#F7F8FA] border border-[#E5E9EE] rounded-md px-4 py-3 outline-none focus:border-[#FCBF49] focus:ring-2 focus:ring-[#FCBF49]/30" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#52606E] mb-1.5 font-semibold">{t("quote_compare.counter.message_label")}</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                  placeholder={t("quote_compare.counter.message_placeholder")}
                  data-testid="qc-counter-message"
                  className="w-full bg-[#F7F8FA] border border-[#E5E9EE] rounded-md px-4 py-3 outline-none focus:border-[#FCBF49] focus:ring-2 focus:ring-[#FCBF49]/30 resize-none" />
              </div>
              <button type="submit" disabled={busy} data-testid="qc-counter-submit"
                className="w-full bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("quote_compare.counter.submit")} <Send className="w-4 h-4" /></>}
              </button>
              <p className="text-xs text-[#52606E] text-center">{t("quote_compare.counter.disclaimer")}</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}


function ShareModal({ token, onClose }) {
  const t = useQ();
  const url = `${window.location.origin}/vergelijking/${token}`;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers / non-https
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success(t("quote_compare.share.copied"));
    setTimeout(() => setCopied(false), 2500);
  };

  const tryNative = async () => {
    if (!navigator.share) return false;
    try {
      await navigator.share({ title: "DoctorSol", text: t("quote_compare.share.wa_text"), url });
      return true;
    } catch { return false; }
  };

  const nativeAvailable = typeof navigator !== "undefined" && !!navigator.share;

  const waText = encodeURIComponent(`${t("quote_compare.share.wa_text")}: ${url}`);
  const emailSubject = encodeURIComponent(t("quote_compare.share.email_subject"));
  const emailBody = `${t("quote_compare.share.email_body")}${encodeURIComponent(url)}`;

  return (
    <div className="fixed inset-0 z-50 bg-[#003049]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="qc-share-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 lg:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-xl font-bold text-[#003049]">{t("quote_compare.share.modal_title")}</h3>
          <button onClick={onClose} className="text-[#52606E] hover:text-[#003049] p-1" data-testid="qc-share-close" aria-label={t("quote_compare.share.close")}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-[#52606E] leading-relaxed mb-5">{t("quote_compare.share.modal_body")}</p>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={url}
            readOnly
            data-testid="qc-share-url"
            className="flex-1 bg-[#F7F8FA] border border-[#E5E9EE] rounded-md px-3 py-2.5 text-sm text-[#003049] outline-none truncate"
            onFocus={(e) => e.target.select()}
          />
          <button onClick={copy} data-testid="qc-share-copy"
            className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2.5 font-semibold text-sm transition ${copied ? "bg-[#1F7A4B] text-white" : "bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049]"}`}>
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? t("quote_compare.share.copied") : t("quote_compare.share.copy")}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <a
            href={`https://wa.me/?text=${waText}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="qc-share-whatsapp"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB755] text-white rounded-md px-4 py-2.5 font-semibold text-sm"
          >
            <MessageCircle className="w-4 h-4" /> {t("quote_compare.share.whatsapp")}
          </a>
          <a
            href={`mailto:?subject=${emailSubject}&body=${emailBody}`}
            data-testid="qc-share-email"
            className="inline-flex items-center justify-center gap-2 bg-[#003049] hover:bg-[#001D2B] text-white rounded-md px-4 py-2.5 font-semibold text-sm"
          >
            <Mail className="w-4 h-4" /> {t("quote_compare.share.email")}
          </a>
        </div>

        {nativeAvailable && (
          <button
            type="button"
            onClick={tryNative}
            data-testid="qc-share-native"
            className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-[#E5E9EE] hover:border-[#003049] text-[#52606E] hover:text-[#003049] rounded-md px-4 py-2.5 font-medium text-sm"
          >
            <Share2 className="w-4 h-4" /> {t("quote_compare.share.more")}
          </button>
        )}
      </div>
    </div>
  );
}


function Footer() {
  const t = useQ();
  return (
    <footer className="bg-[#003049] text-white py-10 mt-auto">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/70">
        <div>{t("quote_compare.footer.copy")}</div>
        <div className="flex gap-5">
          <Link to="/" className="hover:text-white">{t("quote_compare.footer.home")}</Link>
          <Link to="/faq" className="hover:text-white">{t("quote_compare.footer.faq")}</Link>
          <Link to="/over-ons" className="hover:text-white">{t("quote_compare.footer.about")}</Link>
        </div>
      </div>
    </footer>
  );
}
